import { z } from 'zod';
import { Client, BannerSet, User, LoginCredentials, BannerSize, DataFeed } from '../types';

// Client validation schema
export const clientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  role: z.enum(['admin', 'client', 'viewer']),
  bannerSets: z.array(z.any()).optional(),
  createdAt: z.date().optional(),
  lastLogin: z.date().optional(),
  isActive: z.boolean().optional(),
  apiId: z.string().optional(),
  assignedUsers: z.array(z.string()).optional(),
  settings: z.object({
    allowDataFeedUpload: z.boolean(),
    allowExport: z.boolean(),
    allowDeployment: z.boolean().optional(),
    maxBannerSets: z.number(),
    customBranding: z.object({
      logo: z.string().optional(),
      primaryColor: z.string().optional(),
      secondaryColor: z.string().optional(),
    }).optional(),
  }).optional(),
  deploymentSettings: z.object({
    customDomain: z.string().optional(),
    enableCDN: z.boolean(),
    maxBannersPerVehicleType: z.number(),
    allowedVehicleTypes: z.array(z.string()),
    defaultRefreshInterval: z.number(),
  }).optional(),
});

// Element validation schema
const elementSchema = z.object({
  id: z.string(),
  type: z.string(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  rotation: z.number().optional(),
  opacity: z.number().optional(),
  content: z.any(),
  style: z.record(z.any()).optional(),
  dataBinding: z.object({
    field: z.string(),
    format: z.string().optional()
  }).optional()
});

// Banner size validation schema
const bannerSizeSchema = z.object({
  id: z.string(),
  name: z.string(),
  width: z.number(),
  height: z.number(),
  category: z.string(),
  elements: z.array(elementSchema),
  thumbnail: z.string().optional(),
  lastModified: z.date(),
  version: z.number().optional()
});

// Data feed validation schema
const dataFeedSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  description: z.string().optional(),
  url: z.string().url().optional(),
  data: z.record(z.any()),
  lastUpdated: z.date().optional()
});

// Banner Set validation schema
export const bannerSetSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  sizes: z.array(bannerSizeSchema),
  dataFeed: dataFeedSchema.nullable(),
  dataFeedArray: z.array(dataFeedSchema),
  dataFeedUrl: z.string().url().optional(),
  autoRefreshInterval: z.number().min(1).optional(),
  lastRefreshTime: z.date().optional(),
  createdAt: z.date(),
  lastModified: z.date(),
  status: z.enum(['draft', 'active', 'archived']),
  version: z.number().optional(),
  deploymentSettings: z.object({
    autoRefreshInterval: z.number().min(1),
    enableAnalytics: z.boolean(),
    customDomain: z.string().optional(),
    fallbackContent: z.string().optional()
  }).optional()
});

// User validation schema
export const userSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().min(1, 'Name is required'),
  role: z.enum(['super_admin', 'admin', 'client', 'viewer']),
  permissions: z.array(z.object({
    resource: z.enum(['clients', 'banner_sets', 'banners', 'users', 'settings', 'deployments']),
    actions: z.array(z.enum(['create', 'read', 'update', 'delete', 'export', 'deploy'])),
    scope: z.enum(['all', 'own', 'assigned']).optional(),
  })),
  clientId: z.string().optional(),
  isActive: z.boolean(),
  createdAt: z.date().optional(),
  lastLogin: z.date().optional(),
  avatar: z.string().optional(),
});

// Login credentials validation schema
export const loginCredentialsSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

// Validation functions
export const validateClient = (data: unknown): Client => {
  return clientSchema.parse(data);
};

export const validateBannerSet = (bannerSet: BannerSet): boolean => {
  try {
    bannerSetSchema.parse(bannerSet);
    return true;
  } catch (error) {
    console.error('Banner set validation error:', error);
    return false;
  }
};

export const validateUser = (data: unknown): User => {
  return userSchema.parse(data);
};

export const validateLoginCredentials = (data: unknown): LoginCredentials => {
  return loginCredentialsSchema.parse(data);
};

// Response validation
export const validateResponse = <T>(schema: z.ZodType<T>, data: unknown): T => {
  return schema.parse(data);
};

// Error handling for validation
export const handleValidationError = (error: unknown): string => {
  if (error instanceof z.ZodError) {
    return error.errors.map(err => err.message).join(', ');
  }
  return 'Validation failed';
};

export const validateBannerSetUpdates = (updates: Partial<BannerSet>): boolean => {
  try {
    // Create a partial schema that only validates the fields that are present
    const partialSchema = bannerSetSchema.partial();
    partialSchema.parse(updates);
    return true;
  } catch (error) {
    console.error('Banner set updates validation error:', error);
    return false;
  }
};

export const validateBannerSize = (size: BannerSize): boolean => {
  try {
    bannerSizeSchema.parse(size);
    return true;
  } catch (error) {
    console.error('Banner size validation error:', error);
    return false;
  }
};

export const validateDataFeed = (dataFeed: DataFeed): boolean => {
  try {
    dataFeedSchema.parse(dataFeed);
    return true;
  } catch (error) {
    console.error('Data feed validation error:', error);
    return false;
  }
};

// Helper function to get validation errors
export const getValidationErrors = (error: z.ZodError): Record<string, string> => {
  const errors: Record<string, string> = {};
  error.errors.forEach((err) => {
    const path = err.path.join('.');
    errors[path] = err.message;
  });
  return errors;
};

// Helper function to validate banner set updates before saving
export const validateBannerSetForSave = (bannerSet: BannerSet): { isValid: boolean; errors?: Record<string, string> } => {
  try {
    bannerSetSchema.parse(bannerSet);
    return { isValid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        isValid: false,
        errors: getValidationErrors(error)
      };
    }
    return { isValid: false };
  }
}; 